import api from '../api'
import Vue from 'vue'
import { cpf } from 'cpf-cnpj-validator'
import {
  gotToOption,
  isValidMedicalRecordValue
} from '@/utils/attendanceHelper'
import { newFilledModules } from '@/utils/documentHelper'
import { GLASSES_PRESCRIPTION_TYPES } from '@/utils/attendanceTypes'
import { isEqual } from 'lodash-es'
import { getFormArray } from '@/modules/attendance/utils/modulesHelper'
import { dispatchSlackMedicalRecordMessage } from '@/modules/attendance/utils/dispatchSlackHelper'
import {
  preAuthorizeSignatures,
  readCertificate
} from '@/utils/lacuna/lacunaHelper'
import { trackFinishedAttendanceEvents } from '@/utils/mixpanelProcess'
import {
  handleSncrNumberingError,
  handleSncrWarning
} from '@/react-apps/sncr/state/sncrEvents'
import router from '@/router'
import { clearHiddenPatientCpf } from '@/react-apps/attendance/finish/hiddenAtestadoCpf'

const RETRYABLE_HTTP_STATUSES = [408, 409, 425, 429, 500, 502, 503, 504]
const ATTENDANCE_LOAD_RETRY_DELAYS_MS = [200, 500]
const ATTENDANCE_LOG_TAG = '[attendanceStore/getAttendance]'

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const shouldRetryAttendanceLoad = err => {
  const status = err?.response?.status
  return !status || RETRYABLE_HTTP_STATUSES.includes(status)
}

const withRetry = async (fn, delays = []) => {
  let lastError
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await fn(attempt)
    } catch (err) {
      lastError = err
      if (attempt >= delays.length || !shouldRetryAttendanceLoad(err)) {
        throw err
      }
      await wait(delays[attempt])
    }
  }
  throw lastError
}

const getErrorContext = err => ({
  status: err?.response?.status || null,
  code: err?.code || null,
  message: err?.message || null
})

const executeAttendanceStepWithRetry = async ({
  actionName,
  request,
  contextData,
  delays = ATTENDANCE_LOAD_RETRY_DELAYS_MS
}) => {
  return withRetry(async attempt => {
    try {
      const response = await request()
      if (attempt > 0) {
        console.info(
          `${ATTENDANCE_LOG_TAG} ${actionName} recuperado após retry`,
          {
            attempt,
            ...contextData
          }
        )
      }
      return response
    } catch (err) {
      const retryable = shouldRetryAttendanceLoad(err)
      console.warn(`${ATTENDANCE_LOG_TAG} falha ${actionName}`, {
        attempt,
        retryable,
        ...getErrorContext(err),
        ...contextData
      })
      throw err
    }
  }, delays)
}

export default {
  async updateModule(context, module) {
    const form = context.state.attendanceFormModules
    const formArray = getFormArray(form, module)
    if (!formArray || !formArray.length) return
    await context.dispatch('updateAttendanceForm', formArray)
    // await context.dispatch('updateFormModules', form)
  },
  setFalseCanEdit: context => {
    context.state.canEdit = false
  },
  async updateFormModules(context, form = null) {
    if (!form) return
    context.commit('setFormModules', form)
  },
  async getAttendance(context, { clinicId, patientId, attendanceId }) {
    context.commit('clearAttendance')
    context.commit('sidebarStore/setPatient', null)
    await context.dispatch('clearExam')
    await context.dispatch('clearPreConsultation')
    try {
      if (!clinicId || !patientId || !attendanceId) {
        throw new Error(
          'Parâmetros obrigatórios não informados para getAttendance'
        )
      }

      const baseContext = { clinicId, patientId, attendanceId }

      const { data } = await executeAttendanceStepWithRetry({
        actionName: 'getAttendance',
        request: () => api.getAttendance(clinicId, patientId, attendanceId),
        contextData: baseContext
      })
      const attendance = data

      const { data: attendanceForm } = await executeAttendanceStepWithRetry({
        actionName: 'getAttendanceModules',
        request: () => {
          if (!attendance?.id) {
            throw new Error('Atendimento sem id para buscar módulos')
          }
          return api.getAttendanceModules(attendance.form_id, attendance.id)
        },
        contextData: {
          ...baseContext,
          attendanceFormId: attendance?.form_id || null
        }
      })
      context.commit('setFormModules', attendanceForm)
      context.commit('setAttendanceSelectForm', attendanceForm)
      context.commit('setSelectedForm', attendanceForm)

      context.commit('setAttendance', attendance)
      context.commit('resetSavedMedicalRecordTypes')

      if (
        Array.isArray(attendance.medicalRecords) &&
        attendance.medicalRecords.length > 0
      ) {
        attendance.medicalRecords.forEach(mr => {
          if (mr && mr.type) {
            context.commit('markMedicalRecordAsSaved', mr.type)
          }
        })
      }

      // context.commit('populateMedicalRecords')
      context.commit('getTreatmentRequests')

      // Popula o form com os dados do prontuário anterior caso exista
      if (
        Array.isArray(attendance.medicalRecords) &&
        attendance.medicalRecords.length > 0
      ) {
        context.commit('updateAttendanceFormFromMr', attendance)
      }

      // Carrega os dados da última consulta caso o atendimento seja de consulta ou exame
      if (
        [
          'ATTENDANCE',
          ...GLASSES_PRESCRIPTION_TYPES,
          'BV_EXAM',
          'SURGERY'
        ].includes(attendance?.type)
      ) {
        await context.dispatch('getPreConsultation')
        // await context.dispatch('getCidConsultation')
        // await context.commit('findExam')
        if (attendance?.type === 'BV_EXAM') {
          context.dispatch('getBvConsultation')
        }
      } else if (attendance.type === 'PRE_CONSULTATION') {
        await context.dispatch('clearPreConsultation')
      } else if (attendance.type === 'EXAM') {
        await context.dispatch('clearExam')
      }

      if (attendance.status === 'PAUSED') {
        await api.unpauseAttendance(clinicId, attendanceId)
      }
    } catch (err) {
      console.error(`${ATTENDANCE_LOG_TAG} erro final`, {
        clinicId,
        patientId,
        attendanceId,
        ...getErrorContext(err),
        error: err
      })
      Vue.prototype.$toast.error(
        'Não foi possível carregar os dados do atendimento, caso o problema persista entre em contato com o suporte'
      )
    }
  },
  setCustomFormEdit(context, payload) {
    context.commit('setCustomFormEdit', payload)
  },
  closeAttendance(context) {
    context.commit('closeAttendance')
  },
  async clearPreConsultation({ commit, state }) {
    commit('setPreConsultation', null)
  },
  async getPreConsultation({ commit, state }) {
    const patientId = state.attendance?.patient?.id
    if (!patientId) {
      console.warn(
        '[attendanceStore/getPreConsultation] ignorado: sem patient.id no atendimento atual (possível estado inconsistente)'
      )
      return
    }
    commit('setLoading', true)
    try {
      const res = await api.getPatientLastPreConsultation(patientId)
      commit('setPreConsultation', res.data)
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    } finally {
      commit('setLoading', false)
    }
  },
  async getBvConsultation({ commit, state }) {
    commit('setLoading', true)
    try {
      const res = await Vue.prototype.api.getBVClinicalRecord(
        state.attendance?.patient?.id
      )
      commit('setBvConsultation', res.data)
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    } finally {
      commit('setLoading', false)
    }
  },
  async clearExam({ commit }) {
    commit('setExam', null)
  },
  async findExam({ commit, state }) {
    commit('setLoading', true)
    try {
      const { data } = await Vue.prototype.api.findPatientLastExam(
        state.attendance?.patient?.id
      )
      commit('setExam', data)
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    } finally {
      commit('setLoading', false)
    }
  },
  populateMedicalRecords(context) {
    context.commit('populateMedicalRecords')
  },
  getTreatmentRequests(context) {
    context.commit('getTreatmentRequests')
  },
  async finishAttendance(context, payload) {
    // Consulta 3.0 (React) embutida: garante que a fila de autosave React
    // esvazie antes de finalizar (bridge exposto pelo PersistenceProvider).
    // No-op quando a 3.0 não está ativa nesta clínica.
    if (typeof window !== 'undefined' && window.__attendanceV3Flush) {
      try {
        await window.__attendanceV3Flush()
      } catch (e) {
        console.warn('[finishAttendance] flush da fila React falhou', e)
      }
    }

    if (context.state.savingMedicalRecordError) {
      Vue.prototype.$toast.warning(
        'Os dados do prontuário não foram sincronizados. Tente novamente em instantes ou, se o problema continuar, entre em contato com o suporte.'
      )
      return false
    }

    // Snapshot do atendimento no início: se a consulta for limpa do store
    // (navegação/guard de rota) enquanto os `await`s abaixo estão em voo,
    // seguimos operando sobre esta referência em vez de reler o getter (que
    // viraria null) — e usamos o id para não sobrescrever a consulta seguinte.
    const attendanceSnapshot = context.state.attendance
    if (!attendanceSnapshot) {
      Vue.prototype.$toast.warning(
        'Os dados do atendimento ainda estão carregando. Aguarde um instante e tente finalizar novamente.'
      )
      return false
    }

    // Confere só o CPF do documento que é de fato emitido (mesma regra de
    // useFinishAttendance.ts): com a opção desmarcada o campo fica oculto e
    // um CPF velho guardado ali travava a finalização e o reprocesso da
    // Visão Geral sem ir para documento nenhum.
    const atestadosFields = context?.state?.form?.atestadosMedicos?.fields
    const cpfAcompanhante =
      atestadosFields?.declaracaoDeComparecimentoAcompanhante === true
        ? atestadosFields.cpfAcompanhante
        : undefined
    const cpfPaciente =
      atestadosFields?.atestado === true
        ? atestadosFields.cpfPaciente
        : undefined

    if (
      (cpfAcompanhante && !cpf.isValid(cpfAcompanhante)) ||
      (cpfPaciente && !cpf.isValid(cpfPaciente) && !payload.quickPrint)
    ) {
      Vue.prototype.$toast.warning('CPF informado não é válido')
      this.dispatch(`attendance/form/handleActiveModule`, 'atestadosMedicos', {
        root: true
      })
      return false
    }

    const cid = attendanceSnapshot.medicalRecords?.find(el => el.type === 'cid')
    const hasCid =
      cid &&
      cid.value &&
      ((Array.isArray(cid.value) && cid.value.length > 0) ||
        (typeof cid.value === 'string' && cid.value.trim().length > 0))
    if (
      !hasCid &&
      !payload.quickPrint &&
      !payload.noCid &&
      ['ATTENDANCE', 'SURGERY'].find(
        type => type === context.state?.attendance?.type
      )
    ) {
      Vue.prototype.$toast.warning('Informe um CID antes de finalizar')
      const cidForm = context.state?.form?.cid
      if (cidForm) {
        if (!cidForm.showing) {
          this.dispatch(
            `attendance/form/cid/handleProps`,
            {
              key: 'showing',
              value: true
            },
            { root: true }
          )
        }
        if (!cidForm.opened) {
          this.dispatch(
            `attendance/form/cid/handleProps`,
            {
              key: 'opened',
              value: true
            },
            { root: true }
          )
        }
      }
      this.dispatch(`attendance/form/handleActiveModule`, 'cid', { root: true })
      Vue.nextTick(() => {
        setTimeout(() => {
          gotToOption('cid')
        }, 400)
      })
      return false
    }
    const toSignDefault = !payload.noCid ? [0] : []

    const data = {
      attendanceId: attendanceSnapshot.id,
      toPrint: payload.toPrint || [],
      toSign: payload.toSign || [
        ...toSignDefault,
        ...newFilledModules(attendanceSnapshot.medicalRecords)
      ],
      quickPrint: payload.quickPrint || false,
      certContent: null,
      service: null,
      consultationVersion: '2.0'
    }

    const certificate = context.rootState.attendanceStore.certificate
    if (!data.quickPrint) {
      if (attendanceSnapshot.type !== 'PRE_CONSULTATION') {
        if (certificate.active && certificate.type === 1) {
          await preAuthorizeSignatures(
            certificate.cert.thumbprint,
            data.toSign.length
          )
          data.certContent = await readCertificate(certificate.cert.thumbprint)
        } else if (
          certificate.active &&
          certificate.type === 2 &&
          certificate?.cert?.name
        ) {
          // Envia o serviço sempre que a assinatura estiver LIGADA, mesmo com
          // a sessão do provedor expirada (`sessionActive` false): o backend
          // revalida e devolve authentication:'oauth' para reautenticar, em
          // vez de gerar o documento sem assinatura silenciosamente
          // (card 86aje3exn).
          //
          // O `certificate.active` voltou à condição porque ele agora é só a
          // INTENÇÃO do médico — checkCertificate não o sobrescreve mais com
          // o status da sessão. Sem isto, quem desligava a assinatura no
          // perfil continuava sendo redirecionado ao provedor ao finalizar.
          data.service = certificate.cert.name
        }
      }
    }

    const loading = Vue.prototype.$loading.show()
    try {
      const attendance = context.state?.attendance
      const medicalRecords = Array.isArray(attendance?.medicalRecords)
        ? attendance.medicalRecords
        : []
      const savedTypes = context.state.savedMedicalRecordTypes || new Set()

      const pendingTypes = medicalRecords
        .filter(
          mr =>
            mr &&
            mr.type &&
            isValidMedicalRecordValue(mr?.value) &&
            !savedTypes.has(mr.type)
        )
        .map(mr => mr.type)

      if (pendingTypes.length > 0) {
        try {
          await context.dispatch('saveAllPendingForms', pendingTypes)
        } catch (syncErr) {
          loading.hide()
          Vue.prototype.$toast.error(
            'Não foi possível sincronizar todos os dados. Tente novamente.'
          )
          return false
        }
      }

      // Com o atestado desmarcado, o CPF oculto da ficha não pode ir para o
      // cadastro: o backend grava `cpfPaciente` da ficha no paciente ao
      // concluir (simpleFinish*). Limpa o campo antes (mesma regra de
      // useFinishAttendance.ts). Com a 3.0 montada a ficha é da SaveQueue
      // React (o Vuex é um snapshot) e a finalização dela faz essa limpeza.
      const clearedAtestados =
        !data.quickPrint && !window.__attendanceV3Flush
          ? clearHiddenPatientCpf(atestadosFields)
          : null
      if (clearedAtestados) {
        try {
          await api.updateAttendance(
            attendanceSnapshot.clinic?.id,
            attendanceSnapshot.patient?.id,
            {
              action: 'medical-record',
              type: 'atestados-medicos',
              value: clearedAtestados,
              attendanceId: attendanceSnapshot.id
            }
          )
        } catch (clearErr) {
          loading.hide()
          Vue.prototype.$toast.error(
            'Não foi possível salvar a ficha de atestados. Tente finalizar novamente.'
          )
          return false
        }
        this.dispatch(
          'attendance/form/atestadosMedicos/handleFields',
          { key: 'cpfPaciente', value: null },
          { root: true }
        )
        context.commit('updateMedicalRecord', {
          type: 'atestados-medicos',
          value: clearedAtestados
        })
      }

      const res = await Vue.prototype.api.finishAttendance(data)

      if (res.data?.data_was_empty === true) {
        loading.hide()
        try {
          await context.dispatch('saveAllPendingForms')
          Vue.prototype.$toast.warning(
            'Nenhum dado da consulta foi salvo no prontuário. Os dados foram salvos agora; clique em Finalizar novamente para concluir.'
          )
        } catch (saveErr) {
          console.error('saveAllPendingForms error', saveErr)
          const msg =
            saveErr?.response?.data?.message ||
            saveErr?.message ||
            'Não foi possível salvar os dados no servidor. Corrija e tente finalizar novamente.'
          Vue.prototype.$toast.error(msg)
        }
        return false
      }

      // Receita de antimicrobiano saiu sem número do SNCR (permitido até
      // 30/10/2026): convida a obter a numeração antes do bloqueio.
      handleSncrWarning(res.data)

      let pendingCloudSignature = false
      if (res.data.authentication === 'oauth') {
        // Assinatura em nuvem: precisa reautenticar numa aba. Se o navegador
        // bloquear o pop-up, NÃO finaliza (evita gerar documento sem
        // assinatura silenciosamente) — avisa e deixa o médico tentar de novo.
        const authWindow = window.open(res.data.url, '_blank')
        if (!authWindow) {
          loading.hide()
          Vue.prototype.$toast.warning(
            'Não foi possível abrir a janela de assinatura digital. Libere os pop-ups do navegador e finalize novamente.'
          )
          return false
        }
        pendingCloudSignature = true
      }

      if (!data.quickPrint) {
        switch (attendanceSnapshot.type) {
          case 'PRE_CONSULTATION':
            await Vue.prototype.api.simpleFinishPreAttendance(data.attendanceId)
            break
          default:
            await Vue.prototype.api.simpleFinishAttendance(data.attendanceId)
            break
        }

        context.commit('closeAttendance', {
          attendanceId: attendanceSnapshot.id
        })
        context.commit('setAttendanceEndDatetime')
        if (pendingCloudSignature) {
          // A assinatura ainda depende da reautenticação na aba aberta;
          // não anuncia "sucesso" nem tira o médico da tela (o documento
          // assinado chega aqui via Pusher após concluir a assinatura).
          Vue.prototype.$toast.info(
            'Atendimento finalizado. Conclua a assinatura digital na aba que foi aberta.'
          )
        } else {
          Vue.prototype.$toast.success('Atendimento finalizado com sucesso')
        }
      }

      trackFinishedAttendanceEvents(
        attendanceSnapshot,
        context.state.form,
        data
      )
      loading.hide()

      // Com assinatura em nuvem pendente, mantém o médico na tela do
      // atendimento para concluir a assinatura e receber o documento assinado
      // (Pusher). Sem isso, a navegação levaria embora antes de resolver.
      if (!pendingCloudSignature) {
        if (!data.quickPrint && attendanceSnapshot.type === 'ATTENDANCE') {
          if (router.currentRoute.path !== '/visao-geral') {
            router.push(`/visao-geral`)
          }
        } else if (attendanceSnapshot.type === 'PRE_CONSULTATION') {
          if (router.currentRoute.path !== '/pre-consulta') {
            router.push(`/pre-consulta`)
          }
        } else if (!data.quickPrint && attendanceSnapshot.type === 'EXAM') {
          if (router.currentRoute.path !== '/exames') {
            router.push(`/exames`)
          }
        }
      }
      return true
    } catch (err) {
      loading.hide()
      console.error(err)
      // Receita de antimicrobiano assinada sem número do SNCR (422): abre o
      // diálogo global de numeração, que já mostra a mensagem do backend.
      if (handleSncrNumberingError(err)) return false
      Vue.prototype.$toast.error('Erro ao finalizar atendimento')
    }
  },
  clearAttendance(context) {
    context.commit('clearAttendance')
  },
  editAttendance({ commit, state }) {
    if (!state.attendance?.id) return

    commit('editAttendance')
    Vue.prototype.api.openAttendance(state.attendance.id)
  },
  async setAttendanceSelectFormLabel({ commit, getters }, payload) {
    commit('setAttendanceSelectFormLabel', payload)
    const data = getters.getSelectedForm
    try {
      await Vue.prototype.api.updateForm(data.value, {
        name: data.label
      })
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    }
  },
  setAttendanceSelectForm(context, payload) {
    context.commit('setAttendanceSelectForm', payload || null)
    context.commit('mapForms', payload)
    context.commit('populateMedicalRecords')
    context.commit('getTreatmentRequests')
    // context.dispatch('attendanceStore/populateMedicalRecords', null, { root: true })
    // context.dispatch('attendanceStore/getTreatmentRequests', null, { root: true })
  },
  updateAttendanceFormFromMr(context, payload) {
    context.commit('updateAttendanceFormFromMr', payload || null)
  },
  handleActiveModule(context, module) {
    context.commit('handleActiveModule', module)
  },
  updateMedicalRecord: async (context, form) => {
    if (!context.state.canEdit && !form.editingFinishedReport) {
      const currentState =
        context.rootState.attendanceStore.savingMedicalRecordError
      context.commit('setSavingMedicalRecordError', 'Edição bloqueada')
      !currentState &&
        (await dispatchSlackMedicalRecordMessage(
          context?.rootState?.attendanceStore?.attendance?.id,
          {
            canEdit: !!context?.rootState?.attendanceStore?.canEdit,
            editingFinishedReport: !!form.editingFinishedReport
          }
        ))
      return
    }
    context.commit('clearSavingMedicalRecord')
    context.commit('setSavingMedicalRecord', true)
    try {
      const clinicId =
        context?.rootState?.attendanceStore?.attendance?.clinic?.id
      const patientId =
        context?.rootState?.attendanceStore?.attendance?.patient?.id
      const data = {
        action: 'medical-record',
        type: form.id,
        value: form.multiple ? form.value : form.fields,
        attendanceId: context?.rootState?.attendanceStore?.attendance?.id
      }

      // Idempotência (corta o loop de auto-save, ex.: sincronia Óculos↔Refração):
      // pula o PUT apenas quando o valor a salvar é idêntico ao ÚLTIMO valor
      // PERSISTIDO com sucesso. Comparamos contra o snapshot persistido
      // (semeado do servidor em setAttendance e atualizado só após o PUT) e NÃO
      // contra state.attendance.medicalRecords — este é mutado de forma otimista
      // pelas mutations updateMedicalRecord/updateFormValue ANTES deste ponto, o
      // que gerava falso positivo e impedia o óculos de salvar após a sincronia
      // (regressão 86ajg2mt0 / card VISTARA 86ajgf10w).
      const persistedValues = context.state.persistedMedicalRecordValues || {}
      const hasPersistedSnapshot = Object.prototype.hasOwnProperty.call(
        persistedValues,
        data.type
      )
      if (
        hasPersistedSnapshot &&
        isEqual(persistedValues[data.type], data.value)
      ) {
        return
      }

      if (clinicId && patientId && data) {
        await api.updateAttendance(clinicId, patientId, data)
      } else {
        const currentState =
          context?.rootState?.attendanceStore?.savingMedicalRecordError
        context.commit(
          'setSavingMedicalRecordError',
          'Clínica, paciente ou dados inválidos'
        )
        !currentState &&
          (await dispatchSlackMedicalRecordMessage(
            context?.rootState?.attendanceStore?.attendance?.id,
            {
              clinicId,
              patientId,
              data
            }
          ))
        return
      }

      context.commit('updateMedicalRecord', {
        type: data.type,
        value: data.value
      })
      // Avança o snapshot persistido só APÓS o PUT ter sucesso. É isto que move
      // a fonte de verdade da idempotência para frente e corta o loop a partir
      // da 2ª gravação idêntica, sem cegar o 1º save real.
      context.commit('setPersistedMedicalRecordValue', {
        type: data.type,
        value: data.value
      })
      context.commit('markMedicalRecordAsSaved', data.type)
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
      context.commit(
        'setSavingMedicalRecordError',
        'Não foi possível salvar as alterações'
      )
      console.log('ERROR updateMedicalRecord => ', err)
    } finally {
      context.commit('setSavingMedicalRecord', false)
    }
  },
  saveAllPendingForms: async (context, typesToSave = null) => {
    // Consulta 3.0: a SaveQueue React é a ÚNICA escritora dos medical records.
    // O `state.attendance.medicalRecords` daqui é um SNAPSHOT congelado no
    // getAttendance do mount (Index.vue hidrata o Vuex em 2º plano) e a 3.0
    // nunca o atualiza — reenviá-lo sobrescrevia, com dado do load, tudo o que
    // o médico digitou na sessão. Era a perda de dados da anamnese ao sair por
    // "Finalizar depois" em clínica com encaminhamento (o React delega ao
    // PauseAttendanceModal da 2.0, que chamava este flush) e também no
    // "Finalizar e imprimir" (aqui `savedMedicalRecordTypes` fica vazio, então
    // `pendingTypes` virava a consulta inteira). Com a 3.0 montada, "salvar o
    // pendente" é dar flush na fila dela — nada de PUT com dado velho.
    if (typeof window !== 'undefined' && window.__attendanceV3Flush) {
      await window.__attendanceV3Flush()
      return
    }
    const attendance = context.state?.attendance
    const clinicId = attendance?.clinic?.id
    const patientId = attendance?.patient?.id
    if (!clinicId || !patientId) {
      throw new Error('Clínica ou paciente não disponível')
    }
    const medicalRecords = Array.isArray(attendance?.medicalRecords)
      ? attendance.medicalRecords
      : []

    let filteredRecords = medicalRecords.filter(
      mr => mr && mr.type && isValidMedicalRecordValue(mr?.value)
    )

    if (typesToSave && Array.isArray(typesToSave)) {
      filteredRecords = filteredRecords.filter(mr =>
        typesToSave.includes(mr.type)
      )
    }

    const pending = filteredRecords.map(mr => ({
      id: mr.type,
      value: mr.value,
      multiple: Array.isArray(mr.value),
      fields: !Array.isArray(mr.value) ? mr.value : undefined
    }))

    if (pending.length === 0) {
      return
    }
    context.commit('setSavingMedicalRecord', true)
    try {
      const results = await Promise.allSettled(
        pending.map(p =>
          api.updateAttendance(clinicId, patientId, {
            action: 'medical-record',
            type: p.id,
            value: p.multiple ? p.value : p.fields,
            attendanceId: context.state.attendance?.id
          })
        )
      )
      const rejected = results.find(r => r.status === 'rejected')
      if (rejected) {
        const err = rejected.reason
        const msg =
          (err && typeof err === 'object' && err.message) ||
          (err && typeof err === 'string' ? err : null) ||
          'Não foi possível salvar as alterações'
        context.commit('setSavingMedicalRecordError', msg)
        throw new Error(msg)
      }
      for (const p of pending) {
        context.commit('updateMedicalRecord', {
          type: p.id,
          value: p.multiple ? p.value : p.fields
        })
        context.commit('markMedicalRecordAsSaved', p.id)
      }
    } catch (err) {
      if (!context.state.savingMedicalRecordError) {
        context.commit(
          'setSavingMedicalRecordError',
          err?.message || 'Não foi possível salvar as alterações'
        )
      }
      throw err
    } finally {
      context.commit('setSavingMedicalRecord', false)
    }
  },
  destroyMedicalRecord: async (context, module) => {
    if (!context.rootState.attendanceStore.canEdit) return

    try {
      const data = {
        type: module,
        attendanceId: context?.rootState?.attendanceStore?.attendance?.id
      }
      const response = await api.destroyMedicalRecord(data)
      return response
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    }
  },
  handleProps(context, { key, value }) {
    context.commit('handleProps', { key, value })
  },
  async updateForms({ commit, getters }, payload) {
    commit('updateForms', payload)
    const data = getters.getSelectedForm
    try {
      await api.updateForm(data.value, {
        forms: data.forms
      })
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    }
  },
  updateAttendanceForms(context, payload) {
    context.commit('setAttendanceSelectForm', payload || null)
  },
  getAttendanceFiles(context, payload) {
    if (!payload) {
      return
    }
    api
      .getAttendanceDocs(payload)
      .then(res => {
        context.commit('setDocs', res.data)
      })
      .catch(err => {
        console.log('ERR getAttendanceFiles => ', err)
      })
      .finally(() => {
        this.loadingDocs = false
      })
  },
  async updateAttendanceForm({ commit, getters }, payload = null) {
    let forms
    if (payload) {
      forms = payload
    } else {
      forms = getters.getSelectedForm?.forms
      commit('updateForms', forms)
    }
    try {
      const attendanceId = getters.fetchAttendanceData?.attendance_form?.id
      attendanceId &&
        (await api.updateAttendanceForm(attendanceId, { forms: payload }))
    } catch (err) {
      Vue.prototype.$toast.error(err.message)
    }
  },
  updateFormValue(context, { id, value, type }) {
    context.commit('updateFormValue', { id, value, type })
  },

  addFormModule(context, { category, id, value }) {
    context.commit('addFormModule', { category, id, value })
  },
  updateAttendanceFormValue(context, { id, value }) {
    context.commit('updateAttendanceFormValue', { id, value })
  },
  updateModuleVisibility(context, module) {
    context.commit('updateModuleVisibility', module)
    const tempForm =
      context.rootState.attendanceStore.attendance?.attendance_form?.forms
    if (tempForm && tempForm.length) {
      context.dispatch('updateAttendanceForm', tempForm)
    }
  },

  updateCategoryVisibility(context, category) {
    context.commit('updateCategoryVisibility', category)
  },
  changeVisibilityCategoryModule(context, { module, category, status }) {
    context.commit('changeVisibilityCategoryModule', {
      module,
      category,
      status
    })
  },
  setAttendance(context, payload) {
    context.commit('setAttendance', payload)
  },
  setAttendanceFormId(context, payload) {
    context.commit('setAttendanceFormId', payload)
  },
  setDocs(context, payload) {
    context.commit('setDocs', payload)
  },
  setSelectedForm(context, form) {
    context.commit('setSelectedForm', form)
  },
  // Verifica se já existem os módulos no formModules
  async fetchFormModules({ getters, commit, dispatch }) {
    try {
      const { data: form } = await api.getAttendanceModules(
        getters.fetchAttendanceData.form_id,
        getters.fetchAttendanceData.id
      )
      if (Object.keys(form).length) {
        commit('setFormModules', form)
      }
      const formSelected = form.find(item => item.default && item.active)
      commit('setSelectedForm', formSelected)
    } catch (error) {
      console.error(error)
      Vue.prototype.$toast.error('Erro ao buscar os módulos do formulário')
      throw error
    }
  },

  setFormModules(context, payload) {
    context.commit('setFormModules', payload)
  },

  async updateModuleVisibilityOnServer({ getters, dispatch }) {
    try {
      const form = []
      Object.values(getters.getSelectedForm[0]).map(category => {
        Object.values(category.modules).map(module => {
          return form.push({
            customName: module.customName,
            id: module.id,
            opened: module.opened,
            showing: module.showing
          })
        })
      })
      if (form && form.length) {
        await dispatch('updateAttendanceForm', form)
      }
    } catch (error) {
      Vue.prototype.$toast.error(
        'Failed to update form on server: ' + error.message
      )
    }
  },
  addAditionalModule(context, payload) {
    context.commit('addAditionalModule', payload)
  },
  setCertificate(context, payload) {
    context.commit('setCertificate', payload)
  },
  setOculosImporting(context, status) {
    context.commit('setOculosImporting', status)
  }
}
